export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  isPlatformStaff: boolean;
}

export interface SiteSummary {
  id: string;
  name: string;
  tenantId: string;
  tenantName: string;
  role: "owner" | "admin" | "editor" | "viewer" | "platform";
  domains: string[];
  modules: string[];
  businessType: { key: string; name: string } | null;
  availablePlugins: Array<{ key: string; name: string; description: string }>;
}

export interface ContentBlock {
  type: "section" | "hero" | "text" | "image";
  heading: string;
  text: string;
  image_url: string;
  alt: string;
}

export interface SeoSettings {
  title: string;
  description: string;
}

export interface PageSummary {
  id: string;
  title: string;
  path: string;
  updatedAt: string;
  hasDraft: boolean;
}

export interface PageDetail extends PageSummary {
  body: ContentBlock[];
  seo: SeoSettings;
}

export interface PageInput {
  title: string;
  path: string;
  body: ContentBlock[];
  seo: SeoSettings;
}

export interface PublishedRelease {
  id: string;
  number: number;
  publishedAt: string;
}

export interface PagesResponse {
  pages: PageSummary[];
  currentRelease: PublishedRelease | null;
  canEdit: boolean;
}

export interface LabelledValue {
  label: string;
  value: string;
}

export interface DayHours {
  closed: boolean;
  open: string;
  close: string;
}

export type DayName =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type OpeningHours = Record<DayName, DayHours>;

export interface BusinessLocation {
  label: string;
  addressLine1: string;
  addressLine2: string;
  townCity: string;
  county: string;
  postcode: string;
  country: string;
  phone: string;
  email: string;
  openingHours: OpeningHours;
}

export interface BusinessProfile {
  businessName: string;
  description: string;
  phoneNumbers: LabelledValue[];
  emailAddresses: LabelledValue[];
  locations: BusinessLocation[];
  socialLinks: LabelledValue[];
  updatedAt: string | null;
}

export interface MenuItem {
  id?: string;
  name: string;
  description: string;
  price: string;
  calories: number | null;
  isAvailable: boolean;
}

export interface MenuSection {
  id?: string;
  name: string;
  description: string;
  items: MenuItem[];
}

export interface MenuDocument {
  enabled: boolean;
  title: string;
  currency: "GBP" | "EUR" | "USD";
  currencySymbol: string;
  sections: MenuSection[];
  updatedAt: string | null;
}

export interface PortfolioProject {
  id?: string;
  title: string;
  label: string;
  description: string;
  stack: string;
  type: string;
  status: string;
  proofMetric: string;
  slug: string;
  imageUrl: string;
  accent: string;
  projectUrl: string;
  externalUrl: string;
  githubUrl: string;
  seoDescription: string;
  videoUrl: string;
  videoCaption: string;
  content: string[];
  caseStudy: Array<{ label: string; title: string; body: string }>;
  architecture: {
    title?: string;
    description?: string;
    nodes?: Array<{ label: string; detail: string }>;
    flows?: string[];
  };
  features: string[];
  challenges: string[];
  learning: string[];
  postmortem: string[];
  timeline: Array<{ image: string; title: string; caption: string }>;
  isFeatured: boolean;
  isJustLaunched: boolean;
  isPublished: boolean;
}

export interface ProjectsDocument {
  enabled: boolean;
  title: string;
  projects: PortfolioProject[];
  featured: PortfolioProject[];
  justLaunched: PortfolioProject[];
  updatedAt: string | null;
}

export interface ServicePriceItem {
  id?: string;
  name: string;
  shortLabel: string;
  description: string;
  price: string;
  priceSuffix: string;
  durationMinutes: number | null;
  features: string[];
  isFeatured: boolean;
  isPublished: boolean;
}

export interface ServiceChargeItem {
  id?: string;
  name: string;
  description: string;
  kind: "fixed" | "percentage";
  amount: string;
  displayAmount?: string;
  suffix: string;
  isPublished: boolean;
}

export interface ServicesDocument {
  enabled: boolean;
  title: string;
  introduction: string;
  footnote: string;
  items: ServicePriceItem[];
  charges: ServiceChargeItem[];
  updatedAt: string | null;
}

export interface ServiceAreaDocument {
  enabled: boolean;
  title: string;
  introduction: string;
  baseLocation: string;
  coverageSummary: string;
  mapImageUrl: string;
  mapAlt: string;
  includedAreas: string[];
  boundaryNote: string;
  updatedAt: string | null;
}

export interface GalleryItemDocument {
  id?: string;
  kind: "image" | "video";
  mediaUrl: string;
  title: string;
  caption: string;
  altText: string;
  isFeatured: boolean;
  isPublished: boolean;
}

export interface GalleryDocument {
  enabled: boolean;
  title: string;
  introduction: string;
  items: GalleryItemDocument[];
  updatedAt: string | null;
}

export interface UploadedGalleryAsset {
  id: string;
  kind: "image" | "video";
  url: string;
  originalFilename: string;
  width: number | null;
  height: number | null;
  byteSize: number;
}

export interface ContactSettingsDocument {
  enabled: boolean;
  title: string;
  introduction: string;
  recipientEmail: string;
  successMessage: string;
  topics: string[];
  phoneRequired: boolean;
  updatedAt: string | null;
}

export interface ContactSubmissionDocument {
  id: string;
  name: string;
  email: string;
  telephone: string;
  subject: string;
  message: string;
  status: "new" | "read" | "replied" | "spam";
  createdAt: string;
  updatedAt: string;
}

export interface UploadedProjectImage {
  id: string;
  url: string;
  originalFilename: string;
  width: number;
  height: number;
  byteSize: number;
}

export interface BuildMilestone {
  id?: string;
  title: string;
  description: string;
  isComplete: boolean;
}

export interface CurrentBuildEntry {
  id?: string;
  projectId: string;
  projectTitle: string;
  projectSlug: string;
  projectUrl: string;
  externalUrl: string;
  projectType: string;
  accent: string;
  kicker: string;
  headline: string;
  summary: string;
  focusLabel: string;
  milestones: BuildMilestone[];
}

export interface CurrentlyBuildingDocument {
  enabled: boolean;
  availableProjects: Array<{ id: string; title: string; slug: string }>;
  entries: CurrentBuildEntry[];
  updatedAt: string | null;
}

export type ExperimentPresentation = "standard" | "image" | "teaser";

export interface Experiment {
  id?: string;
  title: string;
  label: string;
  paragraphs: string[];
  images: Array<{ url: string; alt: string }>;
  imageUrl: string;
  imageAlt: string;
  externalUrl: string;
  presentation: ExperimentPresentation;
  isPublished: boolean;
}

export interface ExperimentsDocument {
  enabled: boolean;
  experiments: Experiment[];
  updatedAt: string | null;
}

export interface ArchiveProfile {
  id?: string;
  legacyId: number | null;
  slug: string;
  displayName: string;
  registeredName: string;
  countryCode: string;
  publicationState: "draft" | "published" | "archived";
  editorialStatus: string;
  sex: string;
  colour: string;
  foaledDate: string;
  deathDate: string;
  eraLabel: string;
  distanceProfile: string;
  runningStyle: string;
  profile: string;
  irlProfile: string;
  imageUrl: string;
  iconUrl: string;
  irlPhotoUrl: string;
  tags: string[];
  featuredCollections: string[];
  pedigree: Record<string, unknown>;
  connections: Record<string, unknown>;
  career: Record<string, unknown>;
  character: Record<string, unknown>;
  relationships: Record<string, unknown>;
  gallery: unknown[];
  raceResultCount: number;
  mediaCount: number;
}

export interface ArchiveRaceResult {
  id: string;
  legacyId: number;
  name: string;
  nameJp: string;
  nameEn: string;
  grade: string;
  classLevel: string;
  distanceM: number | null;
  surface: string;
  going: string;
  course: string;
  courseJp: string;
  countryCode: string;
  date: string;
  description: string;
  position: number | null;
  time: string;
  margin: string;
  notes: string;
  draw: number | null;
  fieldSize: number | null;
  carriedWeightKg: number | null;
  jockey: string;
  trainer: string;
  startingPrice: string;
  prizeMinor: number | null;
  prizeCurrency: string;
  winnerName: string;
  runnerUpName: string;
  favoriteRank: number | null;
  otherUmaNames: string[];
  sourceRaceUrl: string;
  sourcePageUrl: string;
  reconstruction: ArchiveRaceReconstruction | null;
}

export interface ArchiveRaceReconstruction {
  id: string;
  legacyId: number;
  externalRaceId: string;
  title: string;
  accuracy: string;
  state: string;
  courseKey: string;
  disclosure: string;
  editorNotes: string;
  validation: unknown[];
  runners: Array<Record<string, unknown>>;
  checkpoints: Array<Record<string, unknown>>;
  sectionals: Array<Record<string, unknown>>;
  sources: Array<Record<string, unknown>>;
}

export interface ArchiveFeaturedCollection {
  id: string;
  legacyId: number;
  key: string;
  displayName: string;
}

export interface ArchiveMedia {
  id: string;
  legacyId: number;
  kind: string;
  role: string;
  sortOrder: number;
  storagePath: string;
  mediaUrl: string;
  sourceUrl: string;
  sourcePageUrl: string;
  width: number | null;
  height: number | null;
  caption: string;
  notes: string;
  externalTags: unknown[];
  isShared: boolean;
}

export interface ArchiveProfileDetail extends ArchiveProfile {
  raceResults: ArchiveRaceResult[];
  media: ArchiveMedia[];
  relationshipLinks: Record<string, Array<{ name: string; slug: string }>>;
}

export interface ArchiveAIJob {
  id: string;
  kind: "research" | "write";
  status: "queued" | "in_progress" | "completed" | "failed" | "cancelled";
  model: string;
  output: Record<string, unknown>;
  error: string;
  createdAt: string;
  updatedAt: string;
}

export interface ArchiveAIResearch {
  id: string;
  version: number;
  model: string;
  evidence: Record<string, unknown>;
  sources: Array<{
    title: string;
    url: string;
    publisher: string | null;
    supports: string[];
  }>;
  createdAt: string;
}

export interface ArchiveAIStatus {
  configured: boolean;
  model: string;
  latestResearch: ArchiveAIResearch | null;
  latestDraft: ArchiveAIJob | null;
  jobs: ArchiveAIJob[];
}

export interface ArchiveDocument {
  enabled: boolean;
  title: string;
  description: string;
  collections: ArchiveFeaturedCollection[];
  profiles: ArchiveProfile[];
  updatedAt: string | null;
}

export interface BookingSchedule {
  id?: string;
  locationLabel: string;
  weekday: number;
  startTime: string;
  endTime: string;
  capacity: number;
}

export interface BookingAvailabilityDate {
  id?: string;
  locationLabel: string;
  date: string;
  allDay: boolean;
  startTime: string;
  endTime: string;
}

export interface BookingSettingsDocument {
  enabled: boolean;
  mode: "table" | "appointment";
  autoConfirm: boolean;
  slotIntervalMinutes: number;
  defaultDurationMinutes: number;
  minimumNoticeHours: number;
  maximumAdvanceDays: number;
  maximumPartySize: number;
  notificationEmail: string;
  useDateAvailability: boolean;
  locations: string[];
  schedules: BookingSchedule[];
  dateAvailability: BookingAvailabilityDate[];
  updatedAt: string | null;
}

export interface TableBooking {
  id: string;
  locationLabel: string;
  date: string;
  time: string;
  partySize: number;
  customerName: string;
  telephone: string;
  email: string;
  notes: string;
  status: "pending" | "confirmed" | "declined" | "cancelled";
  createdAt: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { detail?: string };
    throw new ApiError(body.detail ?? "Request failed", response.status);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

async function csrfToken(): Promise<string> {
  const response = await request<{ csrfToken: string }>("/api/auth/csrf/");
  return response.csrfToken;
}

async function mutate<T>(path: string, method: "POST" | "PUT", body: object = {}): Promise<T> {
  const csrf = await csrfToken();
  return request<T>(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": csrf,
    },
    body: JSON.stringify(body),
  });
}

async function uploadFile<T>(path: string, field: string, file: File): Promise<T> {
  const csrf = await csrfToken();
  const body = new FormData();
  body.append(field, file);
  return request<T>(path, {
    method: "POST",
    headers: { "X-CSRFToken": csrf },
    body,
  });
}

export const api = {
  me: () => request<CurrentUser>("/api/auth/me/"),
  sites: () => request<{ sites: SiteSummary[] }>("/api/sites/"),
  pages: (siteId: string) => request<PagesResponse>(`/api/sites/${siteId}/pages/`),
  businessProfile: (siteId: string) =>
    request<{ profile: BusinessProfile; canEdit: boolean }>(
      `/api/sites/${siteId}/business-profile/`,
    ),
  updateBusinessProfile: (siteId: string, profile: BusinessProfile) =>
    mutate<{ profile: BusinessProfile }>(
      `/api/sites/${siteId}/business-profile/`,
      "PUT",
      profile,
    ),
  menu: (siteId: string) =>
    request<{ menu: MenuDocument; canEdit: boolean }>(`/api/sites/${siteId}/menu/`),
  updateMenu: (siteId: string, menu: MenuDocument) =>
    mutate<{ menu: MenuDocument }>(`/api/sites/${siteId}/menu/`, "PUT", menu),
  projects: (siteId: string) =>
    request<{ projects: ProjectsDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/projects/`,
    ),
  updateProjects: (siteId: string, projects: ProjectsDocument) =>
    mutate<{ projects: ProjectsDocument }>(
      `/api/sites/${siteId}/projects/`,
      "PUT",
      projects,
    ),
  services: (siteId: string) =>
    request<{ services: ServicesDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/services/`,
    ),
  updateServices: (siteId: string, services: ServicesDocument) =>
    mutate<{ services: ServicesDocument }>(
      `/api/sites/${siteId}/services/`,
      "PUT",
      services,
    ),
  serviceArea: (siteId: string) =>
    request<{ serviceArea: ServiceAreaDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/service-area/`,
    ),
  updateServiceArea: (siteId: string, serviceArea: ServiceAreaDocument) =>
    mutate<{ serviceArea: ServiceAreaDocument }>(
      `/api/sites/${siteId}/service-area/`,
      "PUT",
      serviceArea,
    ),
  uploadServiceAreaImage: (siteId: string, file: File) =>
    uploadFile<{ image: UploadedProjectImage }>(
      `/api/sites/${siteId}/service-area/image/`,
      "image",
      file,
    ),
  gallery: (siteId: string) =>
    request<{ gallery: GalleryDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/gallery/`,
    ),
  updateGallery: (siteId: string, gallery: GalleryDocument) =>
    mutate<{ gallery: GalleryDocument }>(
      `/api/sites/${siteId}/gallery/`,
      "PUT",
      gallery,
    ),
  uploadGalleryAsset: (siteId: string, file: File) =>
    uploadFile<{ asset: UploadedGalleryAsset }>(
      `/api/sites/${siteId}/gallery/media/`,
      "media",
      file,
    ),
  contactSettings: (siteId: string) =>
    request<{ settings: ContactSettingsDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/contact/settings/`,
    ),
  updateContactSettings: (siteId: string, settings: ContactSettingsDocument) =>
    mutate<{ settings: ContactSettingsDocument }>(
      `/api/sites/${siteId}/contact/settings/`,
      "PUT",
      settings,
    ),
  contactSubmissions: (siteId: string) =>
    request<{ submissions: ContactSubmissionDocument[] }>(
      `/api/sites/${siteId}/contact/submissions/`,
    ),
  updateContactSubmissionStatus: (
    siteId: string,
    submissionId: string,
    status: ContactSubmissionDocument["status"],
  ) =>
    mutate<{ submission: ContactSubmissionDocument }>(
      `/api/sites/${siteId}/contact/submissions/${submissionId}/status/`,
      "PUT",
      { status },
    ),
  uploadProjectImage: (siteId: string, file: File) =>
    uploadFile<{ image: UploadedProjectImage }>(
      `/api/sites/${siteId}/projects/images/`,
      "image",
      file,
    ),
  currentlyBuilding: (siteId: string) =>
    request<{ currentlyBuilding: CurrentlyBuildingDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/currently-building/`,
    ),
  updateCurrentlyBuilding: (siteId: string, document: CurrentlyBuildingDocument) =>
    mutate<{ currentlyBuilding: CurrentlyBuildingDocument }>(
      `/api/sites/${siteId}/currently-building/`,
      "PUT",
      document,
    ),
  experiments: (siteId: string) =>
    request<{ experiments: ExperimentsDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/experiments/`,
    ),
  updateExperiments: (siteId: string, document: ExperimentsDocument) =>
    mutate<{ experiments: ExperimentsDocument }>(
      `/api/sites/${siteId}/experiments/`,
      "PUT",
      document,
    ),
  archive: (siteId: string) =>
    request<{ archive: ArchiveDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/archive/`,
    ),
  updateArchive: (siteId: string, document: ArchiveDocument) =>
    mutate<{ archive: ArchiveDocument }>(
      `/api/sites/${siteId}/archive/`,
      "PUT",
      document,
    ),
  archiveProfile: (siteId: string, profileId: string) =>
    request<{ profile: ArchiveProfileDetail }>(
      `/api/sites/${siteId}/archive/profiles/${profileId}/`,
    ),
  archiveAiStatus: (siteId: string, profileId: string) =>
    request<{ ai: ArchiveAIStatus }>(
      `/api/sites/${siteId}/archive/profiles/${profileId}/ai/`,
    ),
  startArchiveAi: (siteId: string, profileId: string, kind: "research" | "write") =>
    mutate<{ job: ArchiveAIJob }>(
      `/api/sites/${siteId}/archive/profiles/${profileId}/ai/${kind}/`,
      "POST",
    ),
  archiveAiJob: (siteId: string, jobId: string) =>
    request<{ job: ArchiveAIJob }>(`/api/sites/${siteId}/archive/ai/jobs/${jobId}/`),
  applyArchiveAiResearch: (siteId: string, profileId: string) =>
    mutate<{ changed: number; profile: ArchiveProfileDetail }>(
      `/api/sites/${siteId}/archive/profiles/${profileId}/ai/apply-research/`,
      "POST",
    ),
  uploadExperimentImage: (siteId: string, file: File) =>
    uploadFile<{ image: UploadedProjectImage }>(
      `/api/sites/${siteId}/experiments/images/`,
      "image",
      file,
    ),
  bookingSettings: (siteId: string) =>
    request<{ settings: BookingSettingsDocument; canEdit: boolean }>(
      `/api/sites/${siteId}/bookings/settings/`,
    ),
  updateBookingSettings: (siteId: string, settings: BookingSettingsDocument) =>
    mutate<{ settings: BookingSettingsDocument }>(
      `/api/sites/${siteId}/bookings/settings/`,
      "PUT",
      settings,
    ),
  bookings: (siteId: string, date = "") =>
    request<{ bookings: TableBooking[] }>(
      `/api/sites/${siteId}/bookings/${date ? `?date=${encodeURIComponent(date)}` : ""}`,
    ),
  bookingsForRange: (siteId: string, dateFrom: string, dateTo: string) => {
    const query = new URLSearchParams({ dateFrom, dateTo });
    return request<{ bookings: TableBooking[] }>(
      `/api/sites/${siteId}/bookings/?${query.toString()}`,
    );
  },
  updateBookingStatus: (siteId: string, bookingId: string, status: TableBooking["status"]) =>
    mutate<{ booking: TableBooking }>(
      `/api/sites/${siteId}/bookings/${bookingId}/status/`,
      "PUT",
      { status },
    ),
  page: (siteId: string, pageId: string) =>
    request<PageDetail>(`/api/sites/${siteId}/pages/${pageId}/`),
  createPage: (siteId: string, page: PageInput) =>
    mutate<PageDetail>(`/api/sites/${siteId}/pages/`, "POST", page),
  updatePage: (siteId: string, pageId: string, page: PageInput) =>
    mutate<PageDetail>(`/api/sites/${siteId}/pages/${pageId}/`, "PUT", page),
  publishSite: (siteId: string) =>
    mutate<{ release: PublishedRelease }>(`/api/sites/${siteId}/publish/`, "POST"),
  async login(email: string, password: string) {
    const csrf = await csrfToken();
    return request<CurrentUser>("/api/auth/login/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrf,
      },
      body: JSON.stringify({ email, password }),
    });
  },
  async logout() {
    return mutate<void>("/api/auth/logout/", "POST");
  },
};
